"use client"

import { Button } from "@/components/ui/button"
import { themeConfig } from "@/lib/theme-config"
import type { Step } from "@/lib/types"
import { Check, ChevronDown, ChevronRight, ChevronUp, Copy, QrCode, X } from "lucide-react"
import QRCode from "qrcode"
import { useEffect, useState } from "react"
import { DEFAULT_API_ENDPOINT } from "@/lib/default-api-endpoint"
import { ProcessingIcon } from "@/lib/icons"
import StepSkeleton from "./step-skeleton"

interface ProcessingGuideProps {
    productName: string
    apiEndpoint?: string
    useProsourceUrls?: boolean
    compact?: boolean
    uploadedImage?: File | null
}

export default function ProcessingGuide({
    productName,
    apiEndpoint = DEFAULT_API_ENDPOINT,
    useProsourceUrls = false,
    compact = false,
    uploadedImage,
}: ProcessingGuideProps) {
    const baseUrl = apiEndpoint.replace(/\/$/, '')
    const [steps, setSteps] = useState<Step[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isStreamComplete, setIsStreamComplete] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [showQRCode, setShowQRCode] = useState(false)
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
    const [isGeneratingQR, setIsGeneratingQR] = useState(false)
    const [isCopied, setIsCopied] = useState(false)

    useEffect(() => {
        const fetchSteps = async () => {
            try {
                setIsLoading(true)
                setError(null)
                setSteps([])
                setIsStreamComplete(false)
                setIsExpanded(false)

                // Start the stream
                const formData = new FormData()
                formData.append('query', productName)
                if (uploadedImage) {
                    formData.append('image', uploadedImage, uploadedImage.name)
                    formData.append('image_type', uploadedImage.type)
                    formData.append('image_last_modified', uploadedImage.lastModified.toString())
                }
                const response = await fetch(`${baseUrl}/api/generate-processing-guide`, {
                    method: "POST",
                    body: formData,
                })

                if (!response.body) {
                    throw new Error("Stream not available")
                }

                // Create a reader from the stream
                const reader = response.body.getReader()
                const decoder = new TextDecoder()
                let buffer = ""

                // Create a cache to track which steps we've already fetched equipment for
                const processedStepIds = new Set<number>()

                // Read from the stream
                while (true) {
                    const { done, value } = await reader.read()

                    if (done) {
                        // Process any remaining data in the buffer
                        if (buffer.trim()) {
                            try {
                                const steps: Step[] = JSON.parse(buffer.trim())
                                console.log(steps)
                                setSteps((prevSteps) => {
                                    const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                    return steps.map(s => ({
                                        ...s,
                                        equipment: stepsMap.get(s.id)?.equipment || []
                                    }))
                                })
                            } catch (e) {
                                console.error("Error parsing JSON from buffer:", e)
                            }
                        }
                        setIsStreamComplete(true)
                        break
                    }

                    // Decode the chunk and add to buffer
                    const chunk = decoder.decode(value, { stream: true })
                    buffer += chunk

                    // Process complete JSON objects from the buffer
                    const lines = buffer.split("\n")
                    buffer = lines.pop() || "" // Keep the last incomplete line in the buffer

                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const steps: Step[] = JSON.parse(line)
                                // Update steps while preserving existing equipment
                                setSteps((prevSteps) => {
                                    const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                    return steps.map(s => ({
                                        ...s,
                                        equipment: stepsMap.get(s.id)?.equipment || []
                                    }))
                                })

                                // Then fetch equipment for each step that has a description
                                for (const step of steps) {
                                    // Only fetch if we haven't processed this step ID before
                                    if (step.description?.trim() && !processedStepIds.has(step.id)) {
                                        processedStepIds.add(step.id)

                                        // Make the equipment fetch non-blocking
                                        fetch(`${baseUrl}/api/get-related-equipment`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify({ query: step.description })
                                        })
                                            .then(response => response.json())
                                            .then(equipment => {
                                                setSteps((prevSteps) => {
                                                    console.log('Previous steps:', prevSteps)
                                                    console.log('Updating equipment for step:', step.id)
                                                    console.log('New equipment data:', equipment)

                                                    const updatedSteps = prevSteps.map((s) => {
                                                        if (s.id === step.id) {
                                                            const updatedStep = { ...s, equipment: equipment }
                                                            console.log('Updated step:', updatedStep)
                                                            return updatedStep
                                                        }
                                                        return s
                                                    })

                                                    console.log('All updated steps:', updatedSteps)
                                                    return updatedSteps
                                                })
                                            })
                                            .catch(error => {
                                                console.error('Error fetching equipment:', error)
                                            })
                                    }
                                }
                            } catch (e) {
                                console.error("Error parsing JSON:", e, line)
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error streaming processing steps:", err)
                setError("Failed to load processing steps. Please try again.")
            } finally {
                setIsLoading(false)
            }
        }

        fetchSteps()
    }, [productName])

    // Calculate the expected number of steps (for skeleton loading)
    const expectedSteps = 3 // Based on our mock data
    const skeletonCount = Math.max(0, expectedSteps - steps.length)

    // Generate QR code
    const generateQRCode = async () => {
        if (typeof window === 'undefined') return
        
        setIsGeneratingQR(true)
        try {
            const url = window.location.href
            const dataUrl = await QRCode.toDataURL(url, {
                width: 200,
                margin: 2,
                color: {
                    dark: themeConfig.colors.primary,
                    light: '#FFFFFF'
                }
            })
            setQrCodeDataUrl(dataUrl)
            setShowQRCode(true)
        } catch (err) {
            console.error('Error generating QR code:', err)
        } finally {
            setIsGeneratingQR(false)
        }
    }

    // Copy URL to clipboard
    const handleCopyUrl = async () => {
        if (typeof window === 'undefined') return
        
        try {
            await navigator.clipboard.writeText(window.location.href)
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy URL:', err)
        }
    }

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <ProcessingIcon color={themeConfig.colors.primary} size={24} />
                <h3 className="text-xl font-medium" style={{ color: themeConfig.colors.primary }}>
                    {productName} <span className="font-bold italic">processing</span> guide.
                </h3>
            </div>

            {/* Container with minimum height to prevent layout shifts */}
            <div 
                className="space-y-4 min-h-[400px]"
                style={{ 
                    minHeight: isLoading || !isStreamComplete ? '400px' : 'auto',
                    transition: 'min-height 0.3s ease-in-out'
                }}
            >
                {(isExpanded ? steps : steps.slice(0, 2)).map((step) => (
                    <div
                        key={step.id}
                        className="border border-gray-200 rounded-md p-4 animate-fadeIn"
                        style={{
                            backgroundColor: `${themeConfig.colors.primary}05`,
                            minHeight: '120px',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <p className="font-medium mb-2" style={{ color: themeConfig.colors.primary }}>
                            Step {step.id}: {step.description}
                        </p>
                        <div>
                            <p className="text-sm text-gray-600">Equipment Categories:</p>
                            <div className="flex flex-wrap gap-2 mt-1 min-h-[32px]">
                                {step.equipment.map((equipment) => {
                                    return (
                                        <a
                                            href={useProsourceUrls ? (equipment.prosourceUrl || equipment.url) : equipment.url}
                                            target="_blank"
                                            key={equipment.id}
                                            className="px-4 py-2 text-white text-xs rounded-full inline-flex items-center justify-center text-center"
                                            style={{ backgroundColor: themeConfig.colors.primary }}
                                        >
                                            {equipment.name}
                                        </a>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                ))}

                {steps.length > 2 && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-1 text-sm mt-1"
                        style={{ color: themeConfig.colors.primary }}
                    >
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-bold">{isExpanded ? "Collapse steps" : "Expand to see the additional steps..."}</span>
                        {isExpanded
                            ? <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                            : <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                        }
                    </button>
                )}

                {/* Show skeletons for steps that haven't loaded yet */}
                {!isStreamComplete &&
                    skeletonCount > 0 &&
                    Array(skeletonCount)
                        .fill(0)
                        .map((_, i) => <StepSkeleton key={`skeleton-${i}`} />)}

                {error && <p className="text-red-500 py-4">{error}</p>}

                {/* QR Code Section - only show when steps are complete and there are results */}
                {!compact && isStreamComplete && steps.length > 0 && !error && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="text-center">
                            {!showQRCode ? (
                                <Button
                                    onClick={generateQRCode}
                                    disabled={isGeneratingQR}
                                    variant="outline"
                                    className="flex items-center gap-2 mx-auto"
                                    style={{ 
                                        borderColor: themeConfig.colors.primary,
                                        color: themeConfig.colors.primary 
                                    }}
                                >
                                    <QrCode className="h-4 w-4" />
                                    {isGeneratingQR ? 'Generating QR...' : 'Scan QR to open this page on your phone'}
                                </Button>
                            ) : (
                                <div className="inline-block p-6 bg-white border border-gray-200 rounded-lg shadow-lg">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: themeConfig.colors.primary }}>
                                            <QrCode className="h-5 w-5" />
                                            Scan QR Code
                                        </h3>
                                        <button
                                            onClick={() => setShowQRCode(false)}
                                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    
                                    <div className="flex flex-col items-center space-y-4">
                                        <img 
                                            src={qrCodeDataUrl} 
                                            alt="QR Code" 
                                            className="w-48 h-48 border border-gray-200 rounded-lg"
                                        />
                                        <div className="text-center">
                                            <p className="text-sm text-gray-600 mb-3 break-all max-w-xs">
                                                {typeof window !== 'undefined' ? window.location.href : ''}
                                            </p>
                                            <Button
                                                onClick={handleCopyUrl}
                                                variant="outline"
                                                size="sm"
                                                className="flex items-center gap-2"
                                            >
                                                {isCopied ? (
                                                    <>
                                                        <Check className="h-4 w-4" />
                                                        Copied!
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="h-4 w-4" />
                                                        Copy URL
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
